<script setup lang="ts">
    import { computed, reactive, watch } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import FormSelect from '@shared/components/Form/FormSelect.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { saveCategory, type Category } from './AccountSubscriptionCategories.access';
    import { type TimeZone } from './AccountInfo.access';

    const props = defineProps<{
        show: boolean;
        category: Category | null;
        timezones: TimeZone[];
        defaultTimezone: string;
        hasAllowedSendTimesFeature: boolean;
    }>();

    const emit = defineEmits<{
        close: [];
        saved: [category: Category, wasEditing: boolean];
    }>();

    const DAY_BITS = [
        { bit: 2, label: () => $Resources.CronWeekdayMON },
        { bit: 4, label: () => $Resources.CronWeekdayTUE },
        { bit: 8, label: () => $Resources.CronWeekdayWED },
        { bit: 16, label: () => $Resources.CronWeekdayTHU },
        { bit: 32, label: () => $Resources.CronWeekdayFRI },
        { bit: 64, label: () => $Resources.CronWeekdaySAT },
        { bit: 1, label: () => $Resources.CronWeekdaySUN },
    ];

    const state = reactive<{
        name: string;
        description: string;
        allowedDays: number[];
        fromTime: string;
        toTime: string;
        timezone: string;
        isBusy: boolean;
    }>({
        name: '',
        description: '',
        allowedDays: [],
        fromTime: '',
        toTime: '',
        timezone: '',
        isBusy: false,
    });

    const timezoneOptions = computed(() => props.timezones.map((tz) => ({ label: tz.Name, value: tz.TimeZoneId })));
    const isValid = computed(() => !!state.name.trim());

    watch(
        () => props.show,
        (show) => {
            if (!show) return;

            const category = props.category;
            const mask = category?.AllowedDays || 0;
            const hasDefaults = props.hasAllowedSendTimesFeature && !category;

            state.name = category?.Name || '';
            state.description = category?.Description || '';
            state.allowedDays = DAY_BITS.filter((d) => mask & d.bit).map((d) => d.bit);
            state.fromTime = category?.AllowedFromTime || (hasDefaults ? '09:00' : '');
            state.toTime = category?.AllowedToTime || (hasDefaults ? '17:00' : '');
            state.timezone = category?.AllowedTimezone || (hasDefaults ? props.defaultTimezone : '');
        }
    );

    function toggleDay(bit: number, checked: any): void {
        if (checked) {
            if (!state.allowedDays.includes(bit)) state.allowedDays.push(bit);
        } else {
            state.allowedDays = state.allowedDays.filter((d) => d !== bit);
        }
    }

    function clearAllowedSendTimes(): void {
        state.allowedDays = [];
        state.fromTime = '';
        state.toTime = '';
        state.timezone = '';
    }

    function close(): void {
        emit('close');
    }

    async function save(): Promise<void> {
        if (!isValid.value || state.isBusy) return;

        const mask = state.allowedDays.reduce((acc, bit) => acc | bit, 0);
        const hasWindow = state.allowedDays.length > 0 || !!state.fromTime || !!state.toTime || !!state.timezone;

        if (hasWindow && (!state.allowedDays.length || !state.fromTime || !state.toTime || !state.timezone)) {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorCreateHeading, content: $Resources.sendTimeMissingFieldsError });
            return;
        }

        if (state.fromTime && state.toTime && state.fromTime >= state.toTime) {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorCreateHeading, content: $Resources.sendTimeFromAfterToError });
            return;
        }

        const isEditing = !!props.category;
        const payload: Partial<Category> = props.category ? { ...props.category } : {};
        payload.Name = state.name;
        payload.Description = state.description;
        payload.AllowedDays = hasWindow ? mask : null;
        payload.AllowedFromTime = hasWindow ? state.fromTime : null;
        payload.AllowedToTime = hasWindow ? state.toTime : null;
        payload.AllowedTimezone = hasWindow ? state.timezone : null;

        state.isBusy = true;

        try {
            const result = await saveCategory(payload);
            emit('saved', result, isEditing);
            close();
            if (props.category) {
                bsAlert();
            } else {
                bsAlert({
                    type: AlertTypes.Success,
                    title: $Resources.AlertSuccessHeading,
                    content: isEditing ? $Resources.AlertCategoryUpdated : $Resources.AlertCategoryCreated,
                });
            }
        } catch {
            bsAlert({
                type: AlertTypes.Danger,
                title: $Resources.ErrorCreateHeading,
                content: isEditing ? $Resources.ErrorCouldNotUpdateCategory : $Resources.ErrorCouldNotCreateCategory,
            });
        } finally {
            state.isBusy = false;
        }
    }
</script>

<template>
    <Modal size="sm" :show="props.show" :title="props.category ? $Resources.EditCategoryModalTitle : $Resources.NewCategoryModalTitle" @close="close">
        <template #content>
            <FormInput
                class="form-group"
                :label="$Resources.CategoryNameLabel"
                required
                :modelValue="state.name"
                @update:modelValue="state.name = $event"></FormInput>

            <FormInput
                class="form-group"
                :label="$Resources.CategoryDescriptionLabel"
                :modelValue="state.description"
                @update:modelValue="state.description = $event"></FormInput>

            <template v-if="props.hasAllowedSendTimesFeature">
                <h3 class="h5">{{ $Resources.AllowedSendTimesHeading }}</h3>
                <p class="m-lg-b">{{ $Resources.AllowedSendTimesDescription }}</p>

                <div class="form-group d-flex gap-xs flex-direction-col">
                    <FormCheckable
                        v-for="day in DAY_BITS"
                        :key="day.bit"
                        :label="day.label()"
                        :switchStyle="SwitchStyle.Standard"
                        :checked="state.allowedDays.includes(day.bit)"
                        @update:modelValue="toggleDay(day.bit, $event)"></FormCheckable>
                </div>

                <div class="row">
                    <div class="col-xs-6">
                        <FormInput
                            class="form-group"
                            type="time"
                            :label="$Resources.TimeFromLabel"
                            :modelValue="state.fromTime"
                            @update:modelValue="state.fromTime = $event"></FormInput>
                    </div>
                    <div class="col-xs-6">
                        <FormInput
                            class="form-group"
                            type="time"
                            :label="$Resources.TimeToLabel"
                            :modelValue="state.toTime"
                            @update:modelValue="state.toTime = $event"></FormInput>
                    </div>
                </div>

                <FormSelect
                    class="form-group"
                    :label="$Resources.GenericTimeZone"
                    :options="timezoneOptions"
                    :modelValue="state.timezone"
                    @update:modelValue="state.timezone = $event"></FormSelect>

                <div class="form-group text-center m-0-b">
                    <button type="button" class="btn btn-subtle btn-sm" @click="clearAllowedSendTimes">{{ $Resources.GenericClearSettings }}</button>
                </div>
            </template>
        </template>

        <template #button>
            <button type="button" class="btn btn-primary" :class="{ 'is-busy': state.isBusy }" :disabled="!isValid || state.isBusy" @click="save">
                {{ props.category ? $Resources.SaveButton : $Resources.CreateCategoryButton }}
            </button>
        </template>
    </Modal>
</template>

